import { useState, useCallback, useEffect } from 'react';
import InvoiceNavbar from './components/InvoiceNavbar';
import InvoiceFooter from './components/InvoiceFooter';
import EquipmentSelector from './components/EquipmentSelector';
import ClientForm, { ClientInfo } from './components/ClientForm';
import InvoicePreview from './components/InvoicePreview';
import { HireItem } from '../../mocks/hireEquipment';

export interface InvoiceLineItem {
  lineId: string;
  item: HireItem;
  qty: number;
  days: number;
}

function generateInvoiceNumber() {
  const prefix = 'FMR';
  const date = new Date();
  const y = date.getFullYear().toString().slice(-2);
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${y}${m}-${rand}`;
}

let lineIdCounter = 0;
function nextLineId() {
  lineIdCounter += 1;
  return `line-${Date.now()}-${lineIdCounter}`;
}

function calculateBond(lineItems: InvoiceLineItem[]): number {
  // Exclude consumables (ice) and staffing from bond calculation
  const itemsRequiringBond = lineItems.filter(
    (li) => li.item.category !== 'Consumables' && li.item.category !== 'Staffing'
  );
  const uniqueCategories = new Set(itemsRequiringBond.map((li) => li.item.category));
  const bondPerCategory = 100;
  const maxBond = 500;
  return Math.min(uniqueCategories.size * bondPerCategory, maxBond);
}

const DRAFT_STORAGE_KEY = 'fmr-invoice-draft';

interface InvoiceDraft {
  lineItems: InvoiceLineItem[];
  clientInfo: ClientInfo;
  deliveryFee: number;
  setupFee: number;
  discount: number;
  invoiceNumber: string;
  signatureData: { accepted: boolean; signedName: string; signedDate: string };
  savedAt: string;
  paymentStatus?: 'unpaid' | 'deposit_paid' | 'fully_paid';
}

export default function InvoicePage() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);
  const [clientInfo, setClientInfo] = useState<ClientInfo>({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    eventDate: '',
    eventType: '',
    eventVenue: '',
    notes: '',
  });
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [setupFee, setSetupFee] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState(generateInvoiceNumber);
  const [showPrintToast, setShowPrintToast] = useState(false);
  const [signatureData, setSignatureData] = useState<{ accepted: boolean; signedName: string; signedDate: string }>({
    accepted: false,
    signedName: '',
    signedDate: '',
  });
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftInfo, setDraftInfo] = useState<{ savedAt: string; clientName: string; itemCount: number } | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'unpaid' | 'deposit_paid' | 'fully_paid'>('unpaid');

  const securityBond = calculateBond(lineItems);

  // Check for saved draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (raw) {
        const draft: InvoiceDraft = JSON.parse(raw);
        setHasDraft(true);
        setDraftInfo({
          savedAt: draft.savedAt,
          clientName: draft.clientInfo.clientName || 'Unnamed',
          itemCount: draft.lineItems.length,
        });
        setShowLoadModal(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const saveDraft = useCallback(() => {
    const draft: InvoiceDraft = {
      lineItems,
      clientInfo,
      deliveryFee,
      setupFee,
      discount,
      invoiceNumber,
      signatureData,
      savedAt: new Date().toISOString(),
      paymentStatus,
    };
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    setHasDraft(true);
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 2500);
  }, [lineItems, clientInfo, deliveryFee, setupFee, discount, invoiceNumber, signatureData, paymentStatus]);

  const loadDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (raw) {
        const draft: InvoiceDraft = JSON.parse(raw);
        setLineItems(draft.lineItems);
        setClientInfo(draft.clientInfo);
        setDeliveryFee(draft.deliveryFee);
        setSetupFee(draft.setupFee);
        setDiscount(draft.discount);
        setInvoiceNumber(draft.invoiceNumber);
        if (draft.signatureData) setSignatureData(draft.signatureData);
        if (draft.paymentStatus) setPaymentStatus(draft.paymentStatus);
        // Determine which step to go to
        if (draft.clientInfo.clientName && draft.clientInfo.clientEmail && draft.clientInfo.eventDate && draft.lineItems.length > 0) {
          setStep(3);
        } else if (draft.lineItems.length > 0) {
          setStep(2);
        } else {
          setStep(1);
        }
      }
    } catch {
      // ignore
    }
    setShowLoadModal(false);
  }, []);

  const deleteDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setHasDraft(false);
    setDraftInfo(null);
    setShowLoadModal(false);
  }, []);

  const handleAddItem = useCallback((item: HireItem) => {
    const newLine: InvoiceLineItem = {
      lineId: nextLineId(),
      item,
      qty: item.minQty,
      days: 1,
    };
    setLineItems((prev) => [...prev, newLine]);
  }, []);

  const handleRemoveItem = useCallback((lineId: string) => {
    setLineItems((prev) => prev.filter((li) => li.lineId !== lineId));
  }, []);

  const handleUpdateQty = useCallback((lineId: string, qty: number) => {
    setLineItems((prev) => prev.map((li) => (li.lineId === lineId ? { ...li, qty } : li)));
  }, []);

  const handleUpdateDays = useCallback((lineId: string, days: number) => {
    setLineItems((prev) => prev.map((li) => (li.lineId === lineId ? { ...li, days } : li)));
  }, []);

  const subtotal = lineItems.reduce((sum, li) => sum + li.item.price * li.qty * li.days, 0);
  const itemCount = lineItems.length;

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    setShowPrintToast(true);
    setTimeout(() => {
      window.print();
      setShowPrintToast(false);
    }, 300);
  };

  const canProceedStep2 = lineItems.length > 0;
  const canProceedStep3 = clientInfo.clientName.trim() !== '' && clientInfo.clientEmail.trim() !== '' && clientInfo.eventDate !== '';

  return (
    <div className="min-h-screen bg-stone-50">
      <InvoiceNavbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-stone-900 via-stone-950 to-stone-900 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-500"></div>
            <span className="text-amber-400 text-xs font-semibold tracking-[0.3em] uppercase">Melbourne Equipment Hire</span>
          </div>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-4xl md:text-5xl font-display font-normal text-white mb-3">
                Invoice <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-100 to-amber-300">Generator</span>
              </h1>
              <p className="text-white/50 text-base max-w-xl">
                Create professional invoices for your equipment hire. Select items, enter client details, and generate a polished invoice ready to send.
              </p>
            </div>
            {/* Save / Load Buttons in Hero */}
            <div className="flex items-center gap-2 pb-1">
              <button
                onClick={saveDraft}
                disabled={lineItems.length === 0 && !clientInfo.clientName}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  lineItems.length === 0 && !clientInfo.clientName
                    ? 'bg-white/10 text-white/30 cursor-not-allowed'
                    : 'bg-white/15 text-white hover:bg-white/25 border border-white/20'
                }`}
              >
                <i className="ri-save-line"></i>Save Draft
              </button>
              {hasDraft && (
                <button
                  onClick={loadDraft}
                  className="px-4 py-2 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  <i className="ri-folder-open-line"></i>Load Draft
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stepper */}
      <div className="bg-white border-b border-stone-200 sticky top-[68px] z-40">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            {[
              { num: 1, label: 'Select Equipment', icon: 'ri-shopping-bag-line' },
              { num: 2, label: 'Client Details', icon: 'ri-user-line' },
              { num: 3, label: 'Review & Download', icon: 'ri-file-download-line' },
            ].map((s, idx) => (
              <div key={s.num} className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => {
                    if (s.num === 1) setStep(1);
                    if (s.num === 2 && canProceedStep2) setStep(2);
                    if (s.num === 3 && canProceedStep2 && canProceedStep3) setStep(3);
                  }}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    step === s.num
                      ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-white shadow-md'
                      : step > s.num
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-stone-100 text-stone-400'
                  }`}
                >
                  <i className={`${s.icon}`}></i>
                  <span className="hidden sm:inline">{s.label}</span>
                  <span className="sm:hidden">{s.num}</span>
                </button>
                {idx < 2 && <div className={`w-6 sm:w-12 h-px ${step > s.num ? 'bg-amber-400' : 'bg-stone-200'}`}></div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Panel - Form */}
          <div className="lg:col-span-2">
            {step === 1 && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-lg font-display font-semibold text-stone-900">Select Equipment</h2>
                    <p className="text-xs text-stone-400 mt-0.5">Browse and add items to your invoice</p>
                  </div>
                  {itemCount > 0 && (
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                      {itemCount} item{itemCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <EquipmentSelector
                  lineItems={lineItems}
                  onAdd={handleAddItem}
                  onRemove={handleRemoveItem}
                  onUpdateQty={handleUpdateQty}
                  onUpdateDays={handleUpdateDays}
                />

                {/* Security Bond Info */}
                {lineItems.length > 0 && (
                  <div className="mt-4 p-3.5 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-start gap-2.5">
                      <i className="ri-shield-check-line text-amber-600 text-base mt-0.5"></i>
                      <div>
                        <p className="text-xs font-semibold text-amber-800">Security Bond: ${securityBond.toFixed(2)}</p>
                        <p className="text-[10px] text-amber-600 mt-0.5">$100 per equipment category, capped at $500 max. Fully refundable upon safe return of all hire goods.</p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setStep(2)}
                  disabled={!canProceedStep2}
                  className={`mt-6 w-full py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                    canProceedStep2
                      ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-white shadow-lg shadow-amber-500/20 hover:from-amber-400 hover:to-amber-300'
                      : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                  }`}
                >
                  Continue to Client Details
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="mb-5">
                  <h2 className="text-lg font-display font-semibold text-stone-900">Client Details</h2>
                  <p className="text-xs text-stone-400 mt-0.5">Enter the billing and event information</p>
                </div>
                <ClientForm clientInfo={clientInfo} onChange={setClientInfo} />

                {/* Add-ons */}
                <div className="mt-6 p-4 bg-white rounded-xl border border-stone-200">
                  <h3 className="text-sm font-semibold text-stone-800 mb-3">
                    <i className="ri-add-circle-line text-amber-500 mr-1.5"></i>Additional Services
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={deliveryFee > 0}
                          onChange={(e) => setDeliveryFee(e.target.checked ? 150 : 0)}
                          className="w-4 h-4 rounded border-stone-300 text-amber-500 focus:ring-amber-400 cursor-pointer accent-amber-500"
                        />
                        <span className="text-sm text-stone-700">Delivery &amp; Collection</span>
                      </label>
                      <span className="text-sm font-medium text-stone-600">$150</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={setupFee > 0}
                          onChange={(e) => setSetupFee(e.target.checked ? 200 : 0)}
                          className="w-4 h-4 rounded border-stone-300 text-amber-500 focus:ring-amber-400 cursor-pointer accent-amber-500"
                        />
                        <span className="text-sm text-stone-700">Setup &amp; Pack Down</span>
                      </label>
                      <span className="text-sm font-medium text-stone-600">$200</span>
                    </div>
                    <div className="pt-3 border-t border-stone-100">
                      <label className="block text-xs font-medium text-stone-500 mb-1.5">Discount (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={50}
                        value={discount}
                        onChange={(e) => setDiscount(Math.min(50, Math.max(0, parseInt(e.target.value) || 0)))}
                        className="w-24 px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Bond Summary */}
                <div className="mt-4 p-3.5 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-shield-check-line text-amber-600"></i>
                      <span className="text-xs font-semibold text-amber-800">Security Bond</span>
                    </div>
                    <span className="text-sm font-bold text-amber-800">${securityBond.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 rounded-xl text-sm font-medium border border-stone-200 text-stone-600 hover:bg-stone-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-1"></i>Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!canProceedStep3}
                    className={`flex-[2] py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                      canProceedStep3
                        ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-white shadow-lg shadow-amber-500/20 hover:from-amber-400 hover:to-amber-300'
                        : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                    }`}
                  >
                    Review Invoice
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <div className="mb-5">
                  <h2 className="text-lg font-display font-semibold text-stone-900">Invoice Summary</h2>
                  <p className="text-xs text-stone-400 mt-0.5">Review and download your invoice</p>
                </div>

                {/* Summary Card */}
                <div className="bg-white rounded-xl border border-stone-200 p-5 mb-5">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-stone-100">
                    <span className="text-sm text-stone-500">Invoice #</span>
                    <span className="text-sm font-semibold text-stone-800">{invoiceNumber}</span>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Client</span>
                      <span className="text-stone-800 font-medium">{clientInfo.clientName}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Items</span>
                      <span className="text-stone-800 font-medium">{lineItems.length} equipment</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Event</span>
                      <span className="text-stone-800 font-medium">{clientInfo.eventType || 'Not specified'}</span>
                    </div>
                    {clientInfo.eventDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">Date</span>
                        <span className="text-stone-800 font-medium">
                          {new Date(clientInfo.eventDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t border-stone-100">
                      <span className="text-amber-700 font-medium flex items-center gap-1.5">
                        <i className="ri-shield-check-line"></i>Security Bond
                      </span>
                      <span className="text-amber-700 font-bold">${securityBond.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Items List */}
                <div className="bg-white rounded-xl border border-stone-200 p-5 mb-5">
                  <h3 className="text-sm font-semibold text-stone-800 mb-3">Equipment List</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {lineItems.map((li) => (
                      <div key={li.lineId} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                        <div>
                          <p className="text-sm text-stone-700">
                            {li.item.name}
                            {li.item.promo && <span className="ml-1.5 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-bold rounded">PROMO</span>}
                          </p>
                          <p className="text-[10px] text-stone-400">{li.qty} x ${li.item.price.toFixed(2)} x {li.days} day{li.days > 1 ? 's' : ''}</p>
                        </div>
                        <span className="text-sm font-semibold text-stone-800">${(li.item.price * li.qty * li.days).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Client Signature Section */}
                <div className="bg-white rounded-xl border border-stone-200 p-5 mb-5">
                  <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
                    <i className="ri-quill-pen-line text-amber-500"></i>Client Signature
                  </h3>
                  <p className="text-xs text-stone-500 mb-4 leading-relaxed">
                    If the client has accepted the terms, enter their name below to add a digital signature to the invoice.
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={signatureData.accepted}
                        onChange={(e) => {
                          const accepted = e.target.checked;
                          setSignatureData((prev) => ({
                            ...prev,
                            accepted,
                            signedDate: accepted ? new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
                          }));
                        }}
                        className="w-4 h-4 rounded border-stone-300 text-amber-500 focus:ring-amber-400 cursor-pointer accent-amber-500"
                      />
                      <span className="text-sm text-stone-700 group-hover:text-stone-900 transition-colors">Client accepts Terms &amp; Conditions</span>
                    </label>
                    {signatureData.accepted && (
                      <div className="pt-2">
                        <label className="block text-xs font-medium text-stone-500 mb-1.5">Signed Name *</label>
                        <input
                          type="text"
                          value={signatureData.signedName}
                          onChange={(e) => setSignatureData((prev) => ({ ...prev, signedName: e.target.value }))}
                          placeholder="Full name of authorised person"
                          className="w-full px-3.5 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                        />
                        <div className="mt-3 flex items-center gap-2 text-xs text-stone-400">
                          <i className="ri-calendar-check-line"></i>
                          <span>Signed on: {signatureData.signedDate}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Status Tracker */}
                <div className="bg-white rounded-xl border border-stone-200 p-5 mb-5">
                  <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
                    <i className="ri-money-dollar-circle-line text-amber-500"></i>Payment Status
                  </h3>
                  <p className="text-xs text-stone-500 mb-4 leading-relaxed">
                    Track the payment progress for this invoice.
                  </p>
                  <div className="space-y-2">
                    {[
                      { value: 'unpaid' as const, label: 'Unpaid', icon: 'ri-time-line', color: 'rose', desc: 'No payment received yet' },
                      { value: 'deposit_paid' as const, label: 'Deposit Paid', icon: 'ri-coin-line', color: 'amber', desc: '35% deposit has been received' },
                      { value: 'fully_paid' as const, label: 'Fully Paid', icon: 'ri-checkbox-circle-line', color: 'emerald', desc: 'Full payment received' },
                    ].map((opt) => {
                      const isActive = paymentStatus === opt.value;
                      const colorMap: Record<string, { bg: string; border: string; text: string; icon: string; ring: string }> = {
                        rose: { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700', icon: 'text-rose-500', ring: 'ring-rose-200' },
                        amber: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', icon: 'text-amber-500', ring: 'ring-amber-200' },
                        emerald: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', icon: 'text-emerald-500', ring: 'ring-emerald-200' },
                      };
                      const c = colorMap[opt.color];
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setPaymentStatus(opt.value)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer text-left ${
                            isActive
                              ? `${c.bg} ${c.border} ring-2 ${c.ring}`
                              : 'bg-white border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? c.bg : 'bg-stone-100'}`}>
                            <i className={`${opt.icon} text-base ${isActive ? c.icon : 'text-stone-400'}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${isActive ? c.text : 'text-stone-700'}`}>{opt.label}</p>
                            <p className="text-[10px] text-stone-400">{opt.desc}</p>
                          </div>
                          {isActive && (
                            <i className={`ri-check-line text-lg ${c.icon}`}></i>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <button
                    onClick={saveDraft}
                    className="w-full py-3 rounded-xl bg-stone-800 text-white text-sm font-semibold hover:bg-stone-700 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-save-line"></i>
                    Save Invoice Draft
                  </button>
                  <button
                    onClick={handleDownload}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-400 text-white text-sm font-semibold shadow-lg shadow-amber-500/20 hover:from-amber-400 hover:to-amber-300 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-download-line"></i>
                    Download / Print Invoice
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    className="w-full py-3 rounded-xl text-sm font-medium border border-stone-200 text-stone-600 hover:bg-stone-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line mr-1"></i>Edit Details
                  </button>
                  <button
                    onClick={() => {
                      setLineItems([]);
                      setClientInfo({ clientName: '', clientEmail: '', clientPhone: '', clientAddress: '', eventDate: '', eventType: '', eventVenue: '', notes: '' });
                      setDeliveryFee(0);
                      setSetupFee(0);
                      setDiscount(0);
                      setSignatureData({ accepted: false, signedName: '', signedDate: '' });
                      setPaymentStatus('unpaid');
                      setStep(1);
                    }}
                    className="w-full py-3 rounded-xl text-sm font-medium text-stone-400 hover:text-rose-500 hover:bg-rose-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-1"></i>Start New Invoice
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Live Preview */}
          <div className="lg:col-span-3">
            <div className="sticky top-[140px]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-stone-500 uppercase tracking-wider">Live Preview</h2>
                <div className="flex items-center gap-2">
                  {lineItems.length > 0 && (
                    <>
                      <button
                        onClick={saveDraft}
                        className="px-4 py-1.5 rounded-full bg-stone-800 text-white text-xs font-medium hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                      >
                        <i className="ri-save-line"></i>Save
                      </button>
                      <button
                        onClick={handlePrint}
                        className="px-4 py-1.5 rounded-full bg-stone-100 text-stone-600 text-xs font-medium hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                      >
                        <i className="ri-printer-line"></i>Print
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto rounded-2xl">
                <InvoicePreview
                  lineItems={lineItems}
                  clientInfo={clientInfo}
                  invoiceNumber={invoiceNumber}
                  deliveryFee={deliveryFee}
                  setupFee={setupFee}
                  discount={discount}
                  securityBond={securityBond}
                  signatureData={signatureData}
                  paymentStatus={paymentStatus}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      <InvoiceFooter />

      {/* Print Toast */}
      {showPrintToast && (
        <div className="fixed inset-0 bg-black/30 z-[100] flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-printer-line text-amber-600 text-xl"></i>
            </div>
            <p className="text-stone-800 font-semibold mb-1">Preparing Invoice...</p>
            <p className="text-stone-400 text-sm">Your print dialog will open shortly</p>
          </div>
        </div>
      )}

      {/* Save Toast */}
      {showSaveToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] animate-[slideUp_0.3s_ease-out]">
          <div className="bg-stone-900 text-white rounded-xl px-6 py-3.5 shadow-2xl flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <i className="ri-check-line text-emerald-400 text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold">Draft Saved</p>
              <p className="text-xs text-stone-400">Your invoice has been saved locally</p>
            </div>
          </div>
        </div>
      )}

      {/* Load Draft Modal */}
      {showLoadModal && draftInfo && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-3-line text-amber-600 text-xl"></i>
            </div>
            <h3 className="text-lg font-display font-semibold text-stone-900 text-center mb-2">Saved Draft Found</h3>
            <p className="text-sm text-stone-500 text-center mb-5">
              You have an unsaved invoice from a previous session.
            </p>
            <div className="bg-stone-50 rounded-lg p-3.5 mb-5 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-stone-400">Client</span>
                <span className="text-stone-700 font-medium">{draftInfo.clientName}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-stone-400">Items</span>
                <span className="text-stone-700 font-medium">{draftInfo.itemCount} equipment</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-stone-400">Saved</span>
                <span className="text-stone-700 font-medium">
                  {new Date(draftInfo.savedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
            <div className="space-y-2.5">
              <button
                onClick={loadDraft}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-400 text-white text-sm font-semibold shadow-lg shadow-amber-500/20 hover:from-amber-400 hover:to-amber-300 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-folder-open-line"></i>Continue Draft
              </button>
              <button
                onClick={deleteDraft}
                className="w-full py-2.5 rounded-xl text-sm font-medium border border-stone-200 text-stone-500 hover:bg-stone-50 transition-all cursor-pointer whitespace-nowrap"
              >
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Styles + Animation */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }
          html, body {
            width: 210mm;
            height: 297mm;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * { visibility: hidden; }
          #invoice-preview, #invoice-preview * { visibility: visible; }
          #invoice-preview {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 190mm;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          #invoice-preview .invoice-content {
            padding: 4mm !important;
          }
          #invoice-preview .invoice-page-break {
            page-break-inside: avoid;
          }
          #invoice-preview table {
            page-break-inside: auto;
          }
          #invoice-preview tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
        }
        .invoice-a4 {
          max-width: 794px;
          margin: 0 auto;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d6d3d1; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #a8a29e; }
        @keyframes slideUp {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
